import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InlineSpinner } from '../../../core-components/theme/components/Buttons/InlineSpinner';
import { ChoiceChipGroup } from '../../../core-components/theme/components/Fields';
import { InlineAlert } from '../../../core-components/theme/components/InlineAlert';
import { SettingsRow } from '../../../core-components/theme/components/SettingsList';
import { Neutrals } from '../../../core-components/theme/color-palette/colors';
import { LANGUAGE_NAMES } from '../../../i18n/languages';
import type { LanguageChoice } from '../../../i18n/languages';
import { inlineStatusSx, rowFollowUpSx } from '../constants/layout';
import type { LanguageView } from '../domain/languageView';
import { useLanguageChoice } from '../hooks/useLanguageChoice';
import type { LanguageStatus } from '../hooks/useLanguageChoice';

import { languageNoteText } from './languageText';

const optionsOf = (languages: readonly LanguageChoice[]) =>
  languages.map((value) => ({ value, label: <span lang={value}>{LANGUAGE_NAMES[value]}</span> }));

const statusTextSx = { color: 'inherit' } as const;
const noteSx = { color: Neutrals[600] } as const;

const statusText = (t: TFunction, status: LanguageStatus, language: LanguageChoice): string => {
  switch (status) {
    case 'paused':
      return t('common.waitingForConnection', 'Waiting for the connection to come back…');
    case 'saving':
      return t('coreComponents.saveBar.saving', 'Saving…');
    case 'saved':
      return t('account.language.saved', 'Language changed. Alerts will come in {{language}} too.', { language: LANGUAGE_NAMES[language] });
    case 'idle':
    case 'failed':
      return '';
  }
};

const StatusIcon: React.FC<{ status: LanguageStatus }> = ({ status }) => {
  switch (status) {
    case 'paused':
      return <CloudOffOutlinedIcon fontSize="small" aria-hidden />;
    case 'saving':
      return <InlineSpinner aria-hidden />;
    case 'saved':
      return <CheckCircleOutlineIcon fontSize="small" aria-hidden />;
    case 'idle':
    case 'failed':
      return null;
  }
};

export interface LanguageRowProps {
  readonly view: LanguageView;
}

const LanguageNoteLine: React.FC<{ text: string | null }> = ({ text }) => {
  if (text === null) return null;
  return (
    <Typography variant="caption2" component="p" sx={noteSx}>
      {text}
    </Typography>
  );
};

const OnlyLanguageRow: React.FC<{ view: LanguageView }> = ({ view }) => {
  const { t } = useTranslation();
  return (
    <SettingsRow
      label={t('account.language.label', 'Language')}
      value={<span lang={view.current}>{LANGUAGE_NAMES[view.current]}</span>}
      description={languageNoteText(t, view.note)}
    />
  );
};

const LanguagePicker: React.FC<{ view: LanguageView }> = ({ view }) => {
  const { t } = useTranslation();
  const { choose, shown, status, saved } = useLanguageChoice({ current: view.current, timing: 'afterSave' });
  const saving = status === 'saving' || status === 'paused';
  const language = view.current;

  return (
    <SettingsRow label={t('account.language.label', 'Language')}>
      <Box sx={rowFollowUpSx}>
        <ChoiceChipGroup<LanguageChoice>
          label={t('account.language.label', 'Language')}
          hideLabel
          fit="content"
          value={shown}
          frozen={saving}
          onChange={(next) => {
            if (!saving && next !== shown) choose(next);
          }}
          options={optionsOf(view.options)}
        />
        <LanguageNoteLine text={languageNoteText(t, view.note)} />
        <Box role="status" sx={inlineStatusSx}>
          <StatusIcon status={status} />
          <Typography variant="label" component="span" sx={statusTextSx}>
            {statusText(t, status, saved ?? language)}
          </Typography>
        </Box>
        {status === 'failed' && (
          <InlineAlert severity="error" role="alert" message={t('account.language.failed', 'Couldn’t save your language. Pick it again to retry.')} />
        )}
      </Box>
    </SettingsRow>
  );
};

export const LanguageRow: React.FC<LanguageRowProps> = ({ view }) => {
  if (view.options.length === 1) return <OnlyLanguageRow view={view} />;
  return <LanguagePicker view={view} />;
};
